import { ElementType, FC, useEffect, useRef, useState } from "react";

import { ReactionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ControlledMenu, useClick } from "mds/components/Menu";
import { VerticalDivider } from "mds/components/VerticalDivider";
import { ReactionIcon, ReactionVoteNoIcon, ReactionVoteYesIcon } from "mds/icons";
import { cn } from "mds/utils/cn";
import { useAppSelector } from "store";
import {
  selectCurrentClassUserId,
  selectCurrentClassUserReaction,
  selectCurrentClassUserVote,
} from "store/selectors";
import { useAv } from "utils/av";
import { useClassActions } from "utils/class-utils";
import {
  REACTION_ANIMATION_DURATION,
  REACTION_TO_COLOR_CLASSES,
  REACTION_TO_ICON,
  REACTION_TO_TITLE_KEY,
  REMOVE_REACTION_DELAY,
  VOTE_TO_COLOR_CLASSES,
} from "utils/reactions";

type ReactionButtonProps = {
  className?: string;
  Icon: ElementType;
  isSelected?: boolean;
  title: string;
  onClick: () => void;
  eventName?: string;
};

const ReactionButton: FC<ReactionButtonProps> = ({
  className,
  isSelected = false,
  Icon,
  title,
  onClick,
  eventName,
}) => {
  return (
    <Button
      className={cn("p-1.5", className)}
      eventName={eventName}
      isSelected={isSelected}
      title={title}
      onClick={onClick}
    >
      <Icon size="xl" />
    </Button>
  );
};

export const ReactionMenu: FC = () => {
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const { isInVirtualClass } = useAv();
  const currentClassUserReaction = useAppSelector(selectCurrentClassUserReaction);
  const currentClassUserVote = useAppSelector(selectCurrentClassUserVote);
  const { toggleReaction, toggleVote } = useClassActions();
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const anchorProps = useClick(isOpen, setIsOpen);
  const removeReactionTimeout = useRef<NodeJS.Timeout>(null);

  // Auto-clear reaction after animation is complete, plus a delay so the animation isn't cut short
  useEffect(() => {
    if (!currentClassUserReaction || !currentClassUserId) return;

    if (removeReactionTimeout.current) {
      clearTimeout(removeReactionTimeout.current);
    }

    removeReactionTimeout.current = setTimeout(() => {
      toggleReaction(null);
    }, REACTION_ANIMATION_DURATION + REMOVE_REACTION_DELAY);

    return () => {
      if (removeReactionTimeout.current) {
        clearTimeout(removeReactionTimeout.current);
      }
    };
  }, [currentClassUserReaction, currentClassUserId, toggleReaction]);

  return (
    <>
      <div
        className={cn("flex items-center gap-0.5", {
          "rounded-lg border-2 border-black-tint-30 bg-black-tint-30":
            !!currentClassUserVote || !!currentClassUserReaction,
        })}
      >
        <Button
          className={cn({
            "size-10 min-h-10 min-w-10": !!currentClassUserVote || !!currentClassUserReaction,
          })}
          eventName="Classroom Tools - Reactions menu opened"
          isSelected={isOpen}
          kind="squareLegacy"
          ref={ref}
          size="l"
          title={t("reactions.reactions")}
          isSquare
          {...anchorProps}
        >
          <ReactionIcon />
        </Button>
        {currentClassUserReaction && (
          <ReactionButton
            className={REACTION_TO_COLOR_CLASSES[currentClassUserReaction]}
            eventName="Reactions - Clear my reaction"
            Icon={REACTION_TO_ICON[currentClassUserReaction]}
            title={t(REACTION_TO_TITLE_KEY[currentClassUserReaction])}
            onClick={() => {
              toggleReaction(null);
            }}
          />
        )}
        {!currentClassUserReaction && currentClassUserVote === "no" && (
          <ReactionButton
            className={cn({ "size-10": !!currentClassUserVote }, VOTE_TO_COLOR_CLASSES.no)}
            eventName="Reactions - Clear my vote"
            Icon={ReactionVoteNoIcon}
            title={t("reactions.vote_no")}
            onClick={() => {
              toggleVote("no");
            }}
          />
        )}
        {!currentClassUserReaction && currentClassUserVote === "yes" && (
          <ReactionButton
            className={cn({ "size-10": !!currentClassUserVote }, VOTE_TO_COLOR_CLASSES.yes)}
            eventName="Reactions - Clear my vote"
            Icon={ReactionVoteYesIcon}
            title={t("reactions.vote_yes")}
            onClick={() => {
              toggleVote("yes");
            }}
          />
        )}
      </div>

      <ControlledMenu
        anchorRef={ref}
        direction="top"
        state={isOpen ? "open" : "closed"}
        data-dark
        onClose={() => setIsOpen(false)}
      >
        <div className="flex gap-1">
          {isInVirtualClass && (
            <>
              {Object.entries(REACTION_TO_COLOR_CLASSES).map(([reaction, colorClasses]) => (
                <ReactionButton
                  className={colorClasses}
                  eventName={
                    currentClassUserReaction === reaction
                      ? "Reactions - Clear my reaction"
                      : `Reactions - ${reaction}`
                  }
                  Icon={REACTION_TO_ICON[reaction as NonNullable<ReactionType>]}
                  isSelected={currentClassUserReaction === reaction}
                  key={reaction}
                  title={t(REACTION_TO_TITLE_KEY[reaction as NonNullable<ReactionType>])}
                  onClick={() => {
                    toggleReaction(reaction as ReactionType);
                    setIsOpen(false);
                  }}
                />
              ))}
              <VerticalDivider className="m-1.5 h-6 border-black-tint-40" />
            </>
          )}
          <ReactionButton
            className={VOTE_TO_COLOR_CLASSES.no}
            eventName={
              currentClassUserVote === "no" ? "Reactions - Clear my vote" : "Reactions - Vote No"
            }
            Icon={ReactionVoteNoIcon}
            isSelected={currentClassUserVote === "no"}
            title={t("reactions.vote_no")}
            onClick={() => {
              toggleVote("no");
              setIsOpen(false);
            }}
          />
          <ReactionButton
            className={VOTE_TO_COLOR_CLASSES.yes}
            eventName={
              currentClassUserVote === "yes" ? "Reactions - Clear my vote" : "Reactions - Vote Yes"
            }
            Icon={ReactionVoteYesIcon}
            isSelected={currentClassUserVote === "yes"}
            title={t("reactions.vote_yes")}
            onClick={() => {
              toggleVote("yes");
              setIsOpen(false);
            }}
          />
        </div>
      </ControlledMenu>
    </>
  );
};
